import axios from "axios";
import { useEffect, useState } from "react";
import { getGlobalData } from "utils/api";
import { logger } from "@onmoapp/logger";
import { withServerSideProps } from "@/utils/api-logger";
import { getLocalizedPaths } from "utils/localize";
import Image from "next/image";

import { RefreshHeader } from "@/components/brandRefresh/common/PageTemplate";

export default function ContactUs({ metadata, isChatAvailableNow }) {
  const [salesforceScriptReady, setSalesforceScriptReady] = useState(false);

  useEffect(() => {
    if (isChatAvailableNow) {
      const interval = setInterval(() => {
        if (window?.isSalesforceChatLoaded) {
          setSalesforceScriptReady(true);
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, []);

  const handleClick = async () => {
    if (salesforceScriptReady) {
      window.embeddedservice_bootstrap.bootstrapEmbeddedMessaging();
    }
  };
  return (
    <RefreshHeader metadata={metadata}>
      <div className="flex flex-col gap-spacing-12 lg:gap-spacing-14 mb-[100px] text-sm md:text-base 4xl:text-lg">
        <div>
          <p className="text-center">
            Need assistance? We&apos;re here to help! Whether you have questions
            about our products, need guidance with your account, or are looking
            for support, you&apos;re in the right place.
          </p>
        </div>

        <div className="py-5 px-4 text-center bg-onmoPurple-50 text-onmoText rounded-xl flex flex-col lg:flex-row gap-4 lg:gap-6 4xl:gap-3 items-center justify-center">
          <p>
            You can talk to us using our live chat, or you can use phone, email
            or post.
          </p>
          {isChatAvailableNow && (
            <button
              className={`py-3 pl-12 pr-12 rounded-xl disabled:bg-gray-300 bg-onmoPrimary text-white`}
              onClick={handleClick}
              disabled={!salesforceScriptReady}
            >
              Start Live chat
            </button>
          )}
        </div>

        <div className="bg-onmoPurple-50 py-6 px-8 md:py-12 md:px-28 flex justify-center gap-6 rounded-xl flex-col md:flex-row">
          <div className="flex flex-col gap-6 w-full md:w-52">
            <Image
              src="/images/icons/home.svg"
              alt="home-icon"
              className="p-1 h-8 w-8"
              height="32"
              width="32"
            />
            <h4 className="text-2xl text-onmoText">Post</h4>
            <ul className="space-y-1">
              <li>ONMO Ltd</li>
              <li>PO Box 18780</li>
              <li>BIRMINGHAM</li>
              <li>B2 2BN</li>
            </ul>
          </div>

          <div className="flex flex-col gap-6 w-full md:w-52">
            <Image
              src="/images/icons/headphone.svg"
              alt="headphone-icon"
              className="p-1 h-8 w-8"
              height="32"
              width="32"
            />
            <h4 className="text-2xl text-onmoText">Phone</h4>
            <ul className="space-y-1">
              <li>Mon to Fri 9am to 5pm</li>
              <li>Call us: 0330 333 6000</li>
            </ul>
          </div>

          <div className="flex flex-col gap-6 w-full md:w-52">
            <Image
              src="/images/icons/email.svg"
              alt="email-icon"
              className="p-1 h-8 w-8"
              height="32"
              width="32"
            />
            <h4 className="text-2xl text-onmoText">Email</h4>
            <ul className="space-y-1">
              <li>help@onmo.app</li>
              <li>
                Send us an email and we&apos;ll get back to you as soon as we
                can.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </RefreshHeader>
  );
}

export async function getServerSideProps(context) {
  const { locale, locales, defaultLocale, req } = context;
  const globalLocale = await getGlobalData(locale);

  const isChatAvailableNow = await withServerSideProps(
    "/contact-us",
    req.headers,
    async () => {
      try {
        const url = `${process.env.API_BASE_URL}/support-services/live-chat-op-hours`;
        const response = await axios.get(url);
        return response.data.isAvailableNow;
      } catch (err) {
        logger.error("Live-chat availability error");
        logger.error(err);
        return false;
      }
    },
  );

  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: context.resolvedUrl.replace(/\//g, ""),
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  const metadata = {
    metaTitle: `Contact us`,
  };

  return {
    props: {
      metadata,
      global: globalLocale.data,
      isChatAvailableNow,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}
